/**
 * V1 peak-hours adapter — the legacy broker's `settings/peak` payload (and the `peak`
 * body the portal publishes back) in its native nested shape, bridged to the Economy
 * Mode page's Mode/EndMode ring. Ported from the mobile app's
 * `services/mqtt/v1/peakHoursV1Adapter.js` + `peakAdapter.js`, which were confirmed
 * against live captures.
 *
 * Wire shape:
 *
 *   settings/peak →
 *     {
 *       version: 0,
 *       peak: { num, s1, e1, s2, e2, heater, filter, ozone },   // whole hours
 *       mid:  { num, s1, e1, s2, e2, heater, filter, ozone },
 *       off:  { num: null, s1, e1, s2: null, e2: null, heater: null, filter: null, ozone: null },
 *       offset: 0,
 *       sat, sun, mon, tue, wed, thu, fri,                       // booleans
 *     }
 *
 *   controls/spa (under the `peak` wrapper) ←
 *     {
 *       peak: { num, s1, e1[, s2, e2], heater, filter, ozone },
 *       mid:  { num, s1, e1[, s2, e2], heater, filter, ozone },
 *       off:  { num: 1, s1, e1 },
 *       sat, sun, mon, tue, wed, thu, fri,
 *     }
 *
 * Quirks (all from the captures):
 *   - The tub echoes stale `s2`/`e2` even when `num === 1`; only read up to `num`.
 *   - `off.num` arrives as null; always publish `1`. `off` has no toggles.
 *   - A window with start > end crosses midnight (off-peak 19 → 7).
 *   - Coldfire packs refuse a partial message, so the whole schedule goes out every time.
 *
 * Ring model (what the page edits): slice i ends at EndMode[i] in 15-minute units
 * (0..96) and has tariff Mode[i] (1 off-peak, 2 mid-peak, 3 peak); slice 1 wraps from
 * EndMode4. Mode5/6 mirror Mode1 and EndMode5/6 are midnight. Hours ↔ 15-minute units
 * is ×4 / ÷4; the legacy apps draw the pie in whole hours.
 */

export interface V1PeakWindow { start: number; end: number; }

export interface V1PeakBucket {
  num: number;
  windows: V1PeakWindow[];
  heater: boolean;
  filter: boolean;
  ozone: boolean;
}

export interface V1PeakModel {
  peak: V1PeakBucket;
  mid: V1PeakBucket;
  off: V1PeakWindow;
  days: Record<string, boolean>;
}

const DAY_KEYS_WIRE = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_KEYS_MODEL = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MODE_OFF = 1;
const MODE_MID = 2;
const MODE_PEAK = 3;
const MIDNIGHT_15 = 96; // 24h in 15-min units
const INTERVALS_PER_HOUR = 4;

const hoursToIntervals = (h: unknown) => Math.round((Number(h) || 0) * INTERVALS_PER_HOUR) % MIDNIGHT_15;
const intervalsToHours = (em: unknown) => Math.round((Number(em) || 0) / INTERVALS_PER_HOUR) % 24;

/** Parse a raw `settings/peak` payload into the internal model, or null when it is not an object. */
export function parseV1Wire(payload: any): V1PeakModel | null {
  if (!payload || typeof payload !== 'object') return null;

  const parseBucket = (b: any): V1PeakBucket => {
    const src = b && typeof b === 'object' ? b : {};
    const num = src.num === 2 ? 2 : 1;
    const windows: V1PeakWindow[] = [];
    if (src.s1 != null && src.e1 != null) {
      windows.push({ start: Number(src.s1), end: Number(src.e1) });
    }
    if (num === 2 && src.s2 != null && src.e2 != null) {
      windows.push({ start: Number(src.s2), end: Number(src.e2) });
    }
    return {
      num: windows.length,
      windows,
      heater: !!src.heater,
      filter: !!src.filter,
      ozone: !!src.ozone,
    };
  };

  const offSrc = payload.off && typeof payload.off === 'object' ? payload.off : {};
  const off: V1PeakWindow = {
    start: Number(offSrc.s1 ?? 0),
    end: Number(offSrc.e1 ?? 0),
  };

  const days: Record<string, boolean> = {};
  for (let i = 0; i < 7; i++) {
    days[DAY_KEYS_MODEL[i]] = !!payload[DAY_KEYS_WIRE[i]];
  }

  return {
    peak: parseBucket(payload.peak),
    mid: parseBucket(payload.mid),
    off,
    days,
  };
}

/** Serialize the internal model into the `peak` body published to controls/spa (wrapper added by the client). */
export function serializeToV1Wire(model: V1PeakModel): Record<string, any> {
  const serializeBucket = (b: V1PeakBucket) => {
    const num = b?.num === 2 ? 2 : 1;
    const w0 = b?.windows?.[0] || { start: 0, end: 0 };
    const out: Record<string, any> = {
      num,
      s1: w0.start,
      e1: w0.end,
    };
    if (num === 2) {
      const w1 = b?.windows?.[1] || { start: 0, end: 0 };
      out.s2 = w1.start;
      out.e2 = w1.end;
    }
    out.heater = !!b?.heater;
    out.filter = !!b?.filter;
    out.ozone = !!b?.ozone;
    return out;
  };

  const off = model?.off || { start: 0, end: 0 };
  const days = model?.days || {};

  const body: Record<string, any> = {
    peak: serializeBucket(model.peak),
    mid: serializeBucket(model.mid),
    off: {
      num: 1,
      s1: off.start,
      e1: off.end,
    },
  };

  for (let i = 0; i < 7; i++) {
    body[DAY_KEYS_WIRE[i]] = !!days[DAY_KEYS_MODEL[i]];
  }

  return body;
}

// ── READ: V1 PeakSettings → the page's ring ──────────────────────────────────

/**
 * Every window end is a boundary of the ring, and the slice ending there is that
 * window's tariff. Days and toggles come through as 0/1, which `readPeakFields`
 * on the page already accepts.
 */
export function peakV1ToScreen(payload: any): Record<string, any> | null {
  const m = parseV1Wire(payload);
  if (!m) return null;

  const b2i = (v: boolean) => (v ? 1 : 0);

  const segs: { end: number; mode: number }[] = [];
  m.peak.windows.forEach((w) => segs.push({ end: hoursToIntervals(w.end), mode: MODE_PEAK }));
  m.mid.windows.forEach((w) => segs.push({ end: hoursToIntervals(w.end), mode: MODE_MID }));
  segs.push({ end: hoursToIntervals(m.off.end), mode: MODE_OFF });
  segs.sort((a, b) => a.end - b.end);

  const data: Record<string, any> = {
    Mode1: MODE_OFF, Mode2: MODE_OFF, Mode3: MODE_OFF, Mode4: MODE_OFF,
    Mode5: MODE_OFF, Mode6: MODE_OFF,
    EndMode1: 2, EndMode2: 32, EndMode3: 64, EndMode4: 94,
    EndMode5: MIDNIGHT_15, EndMode6: MIDNIGHT_15,
    Mon: b2i(m.days.Mon), Tue: b2i(m.days.Tue), Wed: b2i(m.days.Wed), Thu: b2i(m.days.Thu),
    Fri: b2i(m.days.Fri), Sat: b2i(m.days.Sat), Sun: b2i(m.days.Sun),
    PeakFilter: b2i(m.peak.filter), PeakHeater: b2i(m.peak.heater), PeakOzone: b2i(m.peak.ozone),
    MidPeakFilter: b2i(m.mid.filter), MidPeakHeater: b2i(m.mid.heater), MidPeakOzone: b2i(m.mid.ozone),
  };

  for (let i = 0; i < 4 && i < segs.length; i++) {
    data[`EndMode${i + 1}`] = segs[i].end;
    data[`Mode${i + 1}`] = segs[i].mode;
  }
  data.Mode5 = data.Mode1;
  data.Mode6 = data.Mode5;
  data.EndMode5 = MIDNIGHT_15;
  data.EndMode6 = MIDNIGHT_15;

  return data;
}

// ── WRITE: the page's ring → V1 PeakSettings ────────────────────────────────

/** Ring order from `from`: the window met first after that hour comes first. */
const ringOrder = (windows: V1PeakWindow[], from: number) =>
  [...windows].sort((a, b) => ((a.start - from + 24) % 24) - ((b.start - from + 24) % 24));

/**
 * Walk the ring and file each slice under its tariff. Off-peak is a single window
 * (the first in clock order when the ring has more than one); within a period the
 * windows go out in ring order from the end of off-peak, which is how the legacy
 * portal colours its chart.
 */
export function peakScreenToV1(data: Record<string, any>): Record<string, any> {
  const d = data || {};

  const found: Record<number, V1PeakWindow[]> = { [MODE_PEAK]: [], [MODE_MID]: [], [MODE_OFF]: [] };
  for (let i = 1; i <= 4; i += 1) {
    const list = found[Number(d[`Mode${i}`])];
    if (!list) continue;
    list.push({
      start: intervalsToHours(d[`EndMode${i === 1 ? 4 : i - 1}`]),
      end: intervalsToHours(d[`EndMode${i}`]),
    });
  }

  const off = [...found[MODE_OFF]].sort((a, b) => a.start - b.start)[0] || { start: 0, end: 0 };

  const bucket = (mode: number, toggles: { heater: any; filter: any; ozone: any }): V1PeakBucket => {
    // The wire has room for two windows of a kind, no more.
    const windows = ringOrder(found[mode], off.end).slice(0, 2);
    return {
      num: windows.length,
      windows,
      heater: Number(toggles.heater) > 0 || toggles.heater === true,
      filter: Number(toggles.filter) > 0 || toggles.filter === true,
      ozone: Number(toggles.ozone) > 0 || toggles.ozone === true,
    };
  };

  const day = (v: any) => v === true || Number(v) > 0;

  return serializeToV1Wire({
    peak: bucket(MODE_PEAK, { heater: d.PeakHeater, filter: d.PeakFilter, ozone: d.PeakOzone }),
    mid: bucket(MODE_MID, { heater: d.MidPeakHeater, filter: d.MidPeakFilter, ozone: d.MidPeakOzone }),
    off,
    days: {
      Mon: day(d.Mon), Tue: day(d.Tue), Wed: day(d.Wed), Thu: day(d.Thu),
      Fri: day(d.Fri), Sat: day(d.Sat), Sun: day(d.Sun),
    },
  });
}
